import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Skeleton,
} from '@mui/material'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'
import DataTableSearchField from 'components/SearchField/DataTableSearchField'
import { useCallback, useMemo, useState } from 'react'

export interface IItemList<T> {
    label: string
    value: T
    isPin: boolean
    icon?: any
    disabled?: boolean
}
interface ISelectionDialogProps<T> {
    open: boolean
    initSelectedItem?: T
    items: IItemList<T>[]
    onClose: (val?: T) => void
    hasSearch?: boolean
    title?: string
    isLoading?: boolean
}
function SelectionDialog<T>({
    open,
    onClose,
    initSelectedItem,
    items,
    title = 'Please select',
    hasSearch = false,
    isLoading = false,
}: ISelectionDialogProps<T>) {
    const [queryText, setQueryText] = useState('')
    const pinItems = useMemo(() => {
        return items.filter((i) => i.isPin)
    }, [items])
    const normalItems = useMemo(() => {
        return items.filter((i) => !i.isPin)
    }, [items])
    const [selectedItem, setSelectedItem] = useState(initSelectedItem)
    const filteredItem = useMemo(
        () =>
            normalItems.filter((i) =>
                i
                    .label!.toLocaleLowerCase()
                    .includes(queryText.toLocaleLowerCase()),
            ),
        [normalItems, queryText],
    )
    const handleOnClick = useCallback(
        (value: T) => {
            setSelectedItem(value)
            onClose(value)
        },
        [onClose],
    )
    const handleClose = useCallback(() => {
        onClose(selectedItem)
    }, [onClose, selectedItem])

    function ItemList(m: IItemList<T>, i: number): JSX.Element {
        return (
            <ListItem key={`item-selected-${i}-${m.label}`}>
                <ListItemButton
                    disabled={m.disabled}
                    selected={selectedItem === m.value}
                    onClick={() => {
                        handleOnClick(m.value)
                    }}
                >
                    {m.icon && <ListItemIcon>{m.icon}</ListItemIcon>}
                    <ListItemText primary={m.label} />
                </ListItemButton>
            </ListItem>
        )
    }

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
            <DialogTitle>{title}</DialogTitle>
            <CloseDialogButton
                dialogName="selection dialog"
                onClick={handleClose}
            />
            <DialogContent sx={{ height: hasSearch ? '60vh' : undefined }}>
                {isLoading ? (
                    <List sx={{ padding: 0 }}>
                        <Skeleton height={70} />
                    </List>
                ) : (
                    <List sx={{ padding: 0 }}>
                        {pinItems.map((m, i) => ItemList(m, i))}
                    </List>
                )}
                {hasSearch && (
                    <Box paddingY={1}>
                        <DataTableSearchField
                            value={queryText}
                            onChange={(val) => {
                                setQueryText(val)
                            }}
                        />
                    </Box>
                )}
                <List sx={{ padding: 0 }}>
                    {isLoading
                        ? [1, 2, 3].map((a) => <Skeleton height={70} />)
                        : filteredItem.map((m, i) => ItemList(m, i))}
                </List>
            </DialogContent>
        </Dialog>
    )
}

export default SelectionDialog
